/**
 * Browser dialer wrapping @twilio/voice-sdk Device with a state machine.
 * Modeled on Rolodex's @leadiq/dialer SDK; see
 * .references/DIALER_ARCHITECTURE_IN_ANOTHER_PLACE.md §4.
 *
 * Singleton per tab: every component calling `useDialer()` shares one Device.
 * State machine: idle → calling → ringing → connected → disconnected → idle.
 * The TwiML dials with `answerOnBridge`, so 'ringing' is the prospect's ring and
 * 'connected' is a real answer (person, gatekeeper or machine).
 * Token refresh on `tokenWillExpire`.
 * Device refresh on disconnect to avoid SDK listener leaks (~50 calls).
 * DTMF: sendDigits() feeds the SDK one digit per 250ms, bound to the live Call.
 */

import { computed, readonly, ref, type Ref } from 'vue'
import { Device, type Call } from '@twilio/voice-sdk'
import { fetchAccessToken } from '../services/dialerApi'
import { startRingback, stopRingback } from './ringback'

export type DialerState = 'idle' | 'calling' | 'ringing' | 'connected' | 'disconnected'

/**
 * What the rep should be told, NOT what the SDK observed. The three are kept
 * apart because a wrong label sends the rep chasing the wrong fix: 'mic_silent'
 * is the only one where their own hardware is actually at fault.
*/
export type MediaFault = 'bad_connection' | 'choppy' | 'mic_silent'

/** Per-call media summary, persisted on the dispatch so a bad call is diagnosable later. */
export interface CallMediaStats {
  fault: MediaFault | null
  warnings: string[]
  samples: number
  dead_ms: number
  mos_min: number | null
  jitter_max: number | null
  rtt_max: number | null
  packets_received: number
  /** Which transport the attempt used. Always false today; relay is not wired. */
  relay: boolean
}

const _WARNING_FAULTS: Record<string, MediaFault> = {
  'constant-audio-input-level': 'mic_silent',
  'high-jitter': 'choppy',
  'high-rtt': 'choppy',
  'high-packet-loss': 'choppy',
  'high-packets-lost-fraction': 'choppy',
  'low-mos': 'choppy',
}

// A dead path outranks a noisy one, and both outrank a dead mic.
const _FAULT_PRECEDENCE: readonly MediaFault[] = ['bad_connection', 'mic_silent', 'choppy']

// Counted only once the prospect has answered (the counter resets at the answer),
// so this is three seconds of silence into a live call, not into ringback.
const _SILENT_SAMPLES_FOR_FAULT = 3
const _SAMPLE_INTERVAL_MS = 1000

interface CallParams {
  DispatchId: string
  To: string
}

interface DialerStartedEvent {
  dispatchId: string
  startedAt: Date
}

// ---------------------------------------------------------------------------
// Module-level state — singleton per tab
// ---------------------------------------------------------------------------

const _state: Ref<DialerState> = ref('idle')
const _activeDispatchId: Ref<string | null> = ref(null)
const _activeProspect: Ref<string | null> = ref(null)
const _callStartedAt: Ref<Date | null> = ref(null)
const _callEndedAt: Ref<Date | null> = ref(null)
const _lastError: Ref<string | null> = ref(null)
const _lastErrorCode: Ref<number | null> = ref(null)
const _mediaFault: Ref<MediaFault | null> = ref(null)
const _isReady = ref(false)
const _tick = ref(0)

let _device: Device | null = null
let _activeCall: Call | null = null
let _activeCallEnded: Promise<void> | null = null
let _tokenRefreshInFlight: Promise<string> | null = null
let _tickTimer: ReturnType<typeof setInterval> | null = null
// Hang-up intent latched even if it arrives before the Call object exists (during
// the connect window) — call() honors it the moment the Call resolves.
let _disconnectRequested = false

// A hang-up is a CANCEL/BYE on the Device's signaling socket; destroying the
// Device before it flushes leaves the prospect's leg ringing (or bridged).
const _HANGUP_SETTLE_MS = 400
const _TEARDOWN_MAX_WAIT_MS = 3000

function _sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

// ---------------------------------------------------------------------------
// DTMF
// ---------------------------------------------------------------------------

// The SDK does not queue across sendDigits() calls: each hands its whole string
// to RTCDTMFSender.insertDTMF, which REPLACES the sender's tone buffer. So: one
// digit per send, spaced to exceed the SDK's tone + inter-tone gap (160 + 70ms)
// so the previous digit has left the buffer before the next insert. Those two
// SDK constants are private — useDialer.test.ts pins them.
export const DTMF_SPACING_MS = 250
const _DTMF_DIGITS = /^[0-9*#]+$/

let _dtmfQueue: string[] = []
let _dtmfCall: Call | null = null
let _dtmfTimer: ReturnType<typeof setTimeout> | null = null

function _clearDtmf(): void {
  _dtmfQueue = []
  _dtmfCall = null
  if (_dtmfTimer) {
    clearTimeout(_dtmfTimer)
    _dtmfTimer = null
  }
}

function _drainDtmf(): void {
  _dtmfTimer = null
  const call = _dtmfCall
  // Re-checked on every tick: the far end can hang up, or the rep can, between
  // two digits of one sequence.
  if (!call || call !== _activeCall || _state.value !== 'connected' || _disconnectRequested) {
    _clearDtmf()
    return
  }
  const digit = _dtmfQueue.shift()
  if (digit === undefined) return // cooldown elapsed with nothing queued
  try {
    call.sendDigits(digit)
  } catch (err) {
    console.warn('[useDialer] sendDigits error', err)
  }
  // The cooldown runs whether or not a digit is already waiting, so the spacing
  // never depends on the wall clock.
  _dtmfTimer = setTimeout(_drainDtmf, DTMF_SPACING_MS)
}

// ---------------------------------------------------------------------------
// Media health
// ---------------------------------------------------------------------------

const _activeWarnings = new Set<string>()
let _silentSamples = 0
let _expectInboundMedia = false

function _freshStats(): CallMediaStats {
  return {
    fault: null,
    warnings: [],
    samples: 0,
    dead_ms: 0,
    mos_min: null,
    jitter_max: null,
    rtt_max: null,
    packets_received: 0,
    relay: false,
  }
}

let _stats: CallMediaStats = _freshStats()

function _resetMediaHealth(): void {
  _activeWarnings.clear()
  _silentSamples = 0
  _expectInboundMedia = false
  _stats = _freshStats()
  _mediaFault.value = null
}

// A path that carried audio once is not a dead path, so anything we have ever
// heard — ringback, an announcement, a hello — disqualifies the verdict.
function _isDeadPath(): boolean {
  return (
    _silentSamples >= _SILENT_SAMPLES_FOR_FAULT &&
    _state.value === 'connected' &&
    _stats.packets_received === 0
  )
}

function _recomputeFault(): void {
  if (_isDeadPath()) {
    _mediaFault.value = 'bad_connection'
  } else {
    _mediaFault.value =
      _FAULT_PRECEDENCE.find((fault) =>
        [..._activeWarnings].some((name) => _WARNING_FAULTS[name] === fault),
      ) || null
  }
}

function _accumulateSample(sample: Record<string, unknown>): void {
  _stats.samples += 1
  // The SDK emits mos: null until it has enough RTCP history, and Number(null) is a
  // finite 0 — so anything but `> 0` latches the minimum at zero for the whole call.
  const mos = Number(sample.mos)
  if (mos > 0 && (_stats.mos_min === null || mos < _stats.mos_min)) {
    _stats.mos_min = Math.round(mos * 100) / 100
  }
  const jitter = Number(sample.jitter)
  if (Number.isFinite(jitter) && (_stats.jitter_max === null || jitter > _stats.jitter_max)) {
    _stats.jitter_max = Math.round(jitter)
  }
  const rtt = Number(sample.rtt)
  if (Number.isFinite(rtt) && (_stats.rtt_max === null || rtt > _stats.rtt_max)) {
    _stats.rtt_max = Math.round(rtt)
  }
  const packets = Number(sample.packetsReceived)
  if (Number.isFinite(packets)) {
    _stats.packets_received += packets
    if (packets > 0 && _state.value === 'ringing') stopRingback()
  }

  // SDK 2.18.3 initialises these to 0 during the ICE phase; on 2.18.2 + Chrome
  // 141 they arrive NaN, and `NaN > 0` is false — which would fault every call.
  const received = Number(sample.bytesReceived)
  if (!_expectInboundMedia || !Number.isFinite(received)) return
  _silentSamples = received > 0 ? 0 : _silentSamples + 1
  if (received <= 0) _stats.dead_ms += _SAMPLE_INTERVAL_MS
  _recomputeFault()
}

// ---------------------------------------------------------------------------
// Token management
// ---------------------------------------------------------------------------

async function _getFreshToken(): Promise<string> {
  // Deduplicate concurrent token fetches.
  if (_tokenRefreshInFlight) return _tokenRefreshInFlight
  _tokenRefreshInFlight = fetchAccessToken()
    .then((response) => response.access_token)
    .finally(() => {
      _tokenRefreshInFlight = null
    })
  return _tokenRefreshInFlight
}

// ---------------------------------------------------------------------------
// Device lifecycle
// ---------------------------------------------------------------------------

async function _ensureDevice(): Promise<Device> {
  if (_device) return _device
  const token = await _getFreshToken()
  const device = new Device(token, { logLevel: 'error' })
  // The SDK's own 'outgoing' sound plays for up to 3s on accept — over the first
  // seconds of whoever just answered.
  device.audio?.outgoing(false)
  // Claim the slot before registering so the guards below recognize this
  // device as current while register() is still in flight.
  _device = device
  // A detached device drains in the background for up to a few seconds
  // (_scheduleDeviceTeardown); its late events must not touch shared state.
  device.on('tokenWillExpire', async () => {
    if (_device !== device) return
    try {
      const newToken = await _getFreshToken()
      device.updateToken(newToken)
    } catch (err) {
      console.warn('[useDialer] token refresh failed', err)
    }
  })
  device.on('error', (err) => {
    console.warn('[useDialer] device error', err)
    if (_device !== device) return
    _lastError.value = String(err?.message || err)
    _lastErrorCode.value = Number.isFinite(err?.code) ? Number(err.code) : null
  })
  device.on('registered', () => {
    if (_device !== device) return
    _isReady.value = true
  })
  await device.register().catch((err) => {
    console.warn('[useDialer] device register failed', err)
  })
  return device
}

function _scheduleDeviceTeardown(): void {
  // One Device per call (Rolodex pattern §4.1) so listeners don't accumulate.
  // Detach synchronously — the next call() builds a fresh Device immediately —
  // and destroy in the background once the old call's hang-up signaling has had
  // time to flush.
  const device = _device
  const call = _activeCall
  const ended = _activeCallEnded
  _device = null
  _activeCall = null
  _activeCallEnded = null
  _isReady.value = false
  if (!device) return
  void (async () => {
    if (call && ended) {
      try {
        call.disconnect() // idempotent — makes sure a still-live call is hung up
      } catch {
        /* already closed */
      }
      await Promise.race([
        ended.then(() => _sleep(_HANGUP_SETTLE_MS)),
        _sleep(_TEARDOWN_MAX_WAIT_MS),
      ])
    }
    try {
      device.destroy()
    } catch (err) {
      console.warn('[useDialer] device destroy failed', err)
    }
  })()
}

// ---------------------------------------------------------------------------
// State transitions
// ---------------------------------------------------------------------------

function _transition(next: DialerState): void {
  if (_state.value === next) return
  _state.value = next
  if (next === 'connected') {
    if (!_tickTimer) _tickTimer = setInterval(() => (_tick.value += 1), 1000)
  } else if (_tickTimer) {
    clearInterval(_tickTimer)
    _tickTimer = null
  }
  if (next !== 'ringing') stopRingback()
  if (next !== 'connected') _clearDtmf()
  if (next === 'idle') {
    _activeDispatchId.value = null
    _activeProspect.value = null
    _callStartedAt.value = null
    _callEndedAt.value = null
    _lastError.value = null
    _lastErrorCode.value = null
    _resetMediaHealth()
  }
}

function _attachCallListeners(call: Call): Promise<void> {
  let resolveEnded!: () => void
  const ended = new Promise<void>((resolve) => {
    resolveEnded = resolve
  })
  // A detached call drains for up to a few seconds while the NEXT call is live
  // (_scheduleDeviceTeardown) — its late events must settle the teardown gate
  // but never touch the shared state machine.
  const isCurrent = () => _activeCall === call

  // Repeating events, so .on() — and unlike the once-handlers below they have to
  // be detached by hand when the call ends.
  const onWarning = (name: string) => {
    if (!isCurrent()) return
    // Every warning is kept for diagnosis; only some are worth showing the rep.
    if (!_stats.warnings.includes(name)) _stats.warnings.push(name)
    if (!(name in _WARNING_FAULTS)) return
    _activeWarnings.add(name)
    _recomputeFault()
  }
  const onWarningCleared = (name: string) => {
    if (!isCurrent()) return
    _activeWarnings.delete(name)
    _recomputeFault()
  }
  const onSample = (sample: Record<string, unknown>) => {
    if (!isCurrent()) return
    _accumulateSample(sample)
  }
  const onRinging = (hasEarlyMedia: boolean) => {
    if (!isCurrent()) return
    // Guarded so a late event can't regress an already-connected call.
    const first = _state.value === 'calling'
    if (first) _transition('ringing')
    if (hasEarlyMedia) _expectInboundMedia = true
    // A skip lands before Twilio's cancel, and it can still report ringing in
    // between — a tone for a call already on its way out.
    if (first && !_disconnectRequested) startRingback()
  }
  // The far end alone (the SDK's analyser sits on _remoteStream; our tone is
  // WebAudio straight to destination), at 50ms — so the tone yields inside the
  // first syllable of a greeting rather than talking over it.
  const onVolume = (_input: number, output: number) => {
    if (!isCurrent() || output <= 0 || _state.value !== 'ringing') return
    stopRingback()
  }
  call.on('warning', onWarning)
  call.on('warning-cleared', onWarningCleared)
  call.on('sample', onSample)
  call.on('ringing', onRinging)
  call.on('volume', onVolume)

  const settle = () => {
    call.off('warning', onWarning)
    call.off('warning-cleared', onWarningCleared)
    call.off('sample', onSample)
    call.off('ringing', onRinging)
    call.off('volume', onVolume)
    resolveEnded()
  }
  call.once('accept', () => {
    if (!isCurrent()) return
    _expectInboundMedia = true
    _silentSamples = 0 // Measures silence since the human answered.
    _callStartedAt.value = new Date()
    _transition('connected')
  })
  // IMPORTANT: no Device teardown in these end handlers — destroying the
  // signaling socket in the same tick as a hang-up cut off the CANCEL before it
  // reached Twilio, leaving the <Dial> to the prospect ringing on.
  call.once('disconnect', () => {
    settle()
    if (!isCurrent()) return
    _callEndedAt.value = new Date()
    _transition('disconnected')
    // Disposition modal opens via watcher on 'disconnected' state.
  })
  call.once('cancel', () => {
    settle()
    if (!isCurrent()) return
    _transition('disconnected')
  })
  call.once('error', (err) => {
    settle()
    if (!isCurrent()) return
    _lastError.value = String(err?.message || err)
    _lastErrorCode.value = Number.isFinite(err?.code) ? Number(err.code) : null
    _transition('disconnected')
  })
  call.once('reject', () => {
    settle()
    if (!isCurrent()) return
    _transition('disconnected')
  })
  return ended
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

async function call(params: CallParams): Promise<DialerStartedEvent> {
  if (_state.value !== 'idle' && _state.value !== 'disconnected') {
    throw new Error(`cannot start call from state '${_state.value}'`)
  }
  // Detach the previous call's Device and destroy it in the background.
  _scheduleDeviceTeardown()
  _disconnectRequested = false
  // Clear any error from a prior failed attempt so a retry starts clean (and so
  // the session's watcher doesn't read a stale lastError on the new call).
  _lastError.value = null
  _lastErrorCode.value = null
  _resetMediaHealth()
  _clearDtmf()
  _activeDispatchId.value = params.DispatchId
  _activeProspect.value = params.To
  _transition('calling')

  try {
    // _ensureDevice() can throw — e.g. the tenant has no Twilio creds, so the
    // access-token endpoint 503s, or Device construction fails. It MUST be
    // inside this try so any failure transitions us to 'disconnected' and
    // refreshes the device, never leaving the state stuck in 'calling' (which
    // would wedge every subsequent call() behind the state guard above).
    const device = await _ensureDevice()
    const c = await device.connect({
      params: { DispatchId: params.DispatchId, To: params.To },
    })
    _activeCall = c
    _activeCallEnded = _attachCallListeners(c)
    // A hang-up that landed during the connect window (before _activeCall existed)
    // is honored now, so a too-early hang-up can't leave the call ringing.
    if (_disconnectRequested) {
      try {
        c.disconnect()
      } catch (err) {
        console.warn('[useDialer] deferred hangup error', err)
      } finally {
        _disconnectRequested = false
      }
    }
    return { dispatchId: params.DispatchId, startedAt: new Date() }
  } catch (err) {
    _lastError.value = String((err as Error)?.message || err)
    const code = (err as { code?: unknown })?.code
    _lastErrorCode.value = Number.isFinite(code) ? Number(code) : null
    _transition('disconnected')
    _scheduleDeviceTeardown()
    throw err
  }
}

function hangup(): void {
  // Latch intent even if the Call object doesn't exist yet (mid-connect) so call()
  // can honor it the instant the Call resolves — a too-early hang-up is never lost.
  _disconnectRequested = true
  _clearDtmf()
  if (_activeCall) {
    try {
      _activeCall.disconnect()
    } catch (err) {
      console.warn('[useDialer] hangup error', err)
    }
  }
}

function sendDigits(digits: string): boolean {
  // Only a bridged call can hear a tone: with answerOnBridge there is no media
  // path until the far end answers, and a hang-up in flight makes it moot.
  const call = _activeCall
  if (!call || _state.value !== 'connected' || _disconnectRequested) return false
  if (!_DTMF_DIGITS.test(digits)) return false
  if (_dtmfCall !== call) {
    _dtmfQueue = []
    _dtmfCall = call
  }
  _dtmfQueue.push(...digits)
  if (!_dtmfTimer) _drainDtmf()
  return true
}

function clearDisconnected(): void {
  // Called by the disposition modal after submit so the state machine returns
  // to idle and the modal closes.
  if (_state.value === 'disconnected') {
    _transition('idle')
  }
}

function reset(): void {
  // Force-recover from ANY state: detach the device and return to idle so the
  // next call() is allowed. Used by the session on "Back to queue" / one-off
  // end and as a belt-and-suspenders against a wedged state. The detached
  // device is destroyed in the background once its hang-up signaling has
  // flushed.
  _scheduleDeviceTeardown()
  stopRingback()
  _clearDtmf()
  _transition('idle')
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useDialer() {
  return {
    state: readonly(_state),
    activeDispatchId: readonly(_activeDispatchId),
    activeProspect: readonly(_activeProspect),
    callStartedAt: readonly(_callStartedAt),
    callEndedAt: readonly(_callEndedAt),
    lastError: readonly(_lastError),
    lastErrorCode: readonly(_lastErrorCode),
    mediaFault: readonly(_mediaFault),
    isReady: readonly(_isReady),

    isIdle: computed(() => _state.value === 'idle'),
    isCalling: computed(() => _state.value === 'calling'),
    isRinging: computed(() => _state.value === 'ringing'),
    isConnected: computed(() => _state.value === 'connected'),
    isDisconnected: computed(() => _state.value === 'disconnected'),

    // Talk time: starts when the prospect answers (answerOnBridge), frozen at hangup.
    durationSeconds: computed(() => {
      void _tick.value // subscribe to the clock so this re-computes while live
      const start = _callStartedAt.value
      const end = _callEndedAt.value || (_state.value === 'connected' ? new Date() : null)
      if (!start || !end) return 0
      return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000))
    }),

    // Snapshot for the /end and /release payloads — read at hang-up, before the
    // next call() resets it. `fault` is the state AT hang-up, deliberately not
    // sticky: a call that lost audio for 3s and recovered is not a faulted call.
    mediaStats: (): CallMediaStats => ({
      ..._stats,
      fault: _mediaFault.value,
      warnings: [..._stats.warnings],
    }),

    call,
    hangup,
    sendDigits,
    clearDisconnected,
    reset,
  }
}
